/**
 * Aus Reihen wird eine Matrix (HM-054).
 *
 * **Reine Rechnung — hier kommt keine Zeichenbibliothek vor.** Das ist die
 * Stelle, an der die fachlichen Entscheidungen fallen: was gestapelt wird, wo
 * ein Band liegt, welche Reihe welche Farbe trägt und in welcher Reihenfolge
 * gezeichnet wird. Alles davon lässt sich ohne Browser prüfen; was danach
 * kommt, ist nur noch Übergabe an uPlot.
 *
 * **Die Spalte ist die Einheit, nicht der Punkt.** Eine Zeichenbibliothek für
 * Zeitreihen erwartet gleich lange Spalten mit einer gemeinsamen Zeitachse —
 * `align.ts` stellt sie her, diese Datei ordnet sie an.
 */
import { alignSeries } from "./align.js";
import { BUCKET_MS } from "./buckets.js";
import { seriesColors, type SeriesColorSource } from "./palette.js";
import { stackValues } from "./stack.js";
import type { ChartProps, PointQuality, SeriesMark } from "./types.js";

/** Eine Reihe und ihr Platz in der Matrix. */
export interface SeriesSlot {
  /** Der Index in `data` — Spalte 0 ist die Zeit. */
  readonly slot: number;
  readonly id: string;
  readonly label: string;
  readonly color: string;
  readonly mark: SeriesMark;
  /**
   * Bei einem Stapel: der Slot der Reihe **direkt darunter** — die Zahl, die von
   * dieser abzuziehen ist, um ihren eigenen Wert zu bekommen. `null` für die
   * unterste Reihe und für jedes ungestapelte Diagramm, wo in der Spalte
   * bereits der eigene Wert steht.
   */
  readonly beneath: number | null;
  /**
   * Die Abdeckung je Zeitpunkt (HM-105) — die **eigene** der Reihe, auch im
   * Stapel: Eine Teilsumme bleibt eine, egal was darunter liegt.
   */
  readonly coverage: readonly (number | null)[];
  /** Der Status je Zeitpunkt, `null` für gemessen oder fehlend (HM-115). */
  readonly quality: readonly (Exclude<PointQuality, "measured"> | null)[];
  /** Die Achse (HM-137) — rechts steht eine Linie auf fester Skala. */
  readonly axis: "left" | "right";
  readonly dashed: boolean;
}

/** Ein Spannenband, als Paar von Spalten. */
export interface BandSlot {
  readonly high: number;
  readonly low: number;
  readonly color: string;
}

export interface ChartFrame {
  /** Spalte 0 ist die Zeit in Millisekunden, danach folgen die Reihen. */
  readonly data: readonly (readonly (number | null)[])[];
  /** Die Reihen in **Zeichenreihenfolge**. */
  readonly slots: readonly SeriesSlot[];
  /** Dieselben Reihen in der Reihenfolge, in der der Aufrufer sie genannt hat. */
  readonly rows: readonly SeriesSlot[];
  readonly bands: readonly BandSlot[];
  /** Ob überhaupt ein Wert vorliegt. */
  readonly empty: boolean;
  /**
   * Ob die y-Achse an der Null verankert wird. Balken und Stapel messen ihre
   * Länge ab der Grundlinie; liegt die woanders, lügt jeder Größenvergleich.
   */
  readonly baseline: "auto" | "zero";
  /** Ob gestapelt wurde — die Werte in `data` sind dann Summen. */
  readonly stacked: boolean;
  /**
   * Bei einem Stapel: der Slot, in dem die **Höhe des Stapels** steht — die
   * oberste Reihe trägt die Summe aller darunter. `null` ohne Stapelung: Dort
   * wäre eine Summe über Reihen, die nichts miteinander zu tun haben, eine
   * Rechnung, die niemand bestellt hat (Regel 9).
   */
  readonly total: number | null;
  /**
   * Ob **alle** Reihen der linken Achse Balken sind — dann ist jeder Zeitpunkt
   * ein Abschnitt: Das Ablesefenster trifft den Balken unter dem Zeiger, die
   * Achse beschriftet Tage und Monate mittig (HM-099). Halb aus Balken, halb aus
   * Linien gibt es links nicht; ein Mischbild bekommt die Regeln der Linie. Die
   * Linie der **rechten** Achse ändert daran nichts (HM-137): Sie steht dann in
   * der Mitte ihres Abschnitts, über dem Balken, zu dem sie gehört.
   */
  readonly bucketed: boolean;
  /**
   * Je Zeitpunkt der unsicherste Status über alle Reihen — `null`, wo keine
   * Reihe dort einen Ersatzwert trägt (HM-115). Die Leiste unter dem Abschnitt
   * gilt dem Abschnitt, nicht einer Reihe: Im Stapel aus PV- und Netzanteil
   * sind beide Ersatzwerte, sobald der Verbrauch darunter einer ist.
   */
  readonly substituted: readonly (Exclude<PointQuality, "measured"> | null)[];
}

const SUBSTITUTE_ORDER: readonly Exclude<PointQuality, "measured">[] = ["interpolated", "estimated", "suspect"];

/** Der unsicherere zweier Status — `null` heißt gemessen. */
export function lessCertain(
  left: Exclude<PointQuality, "measured"> | null,
  right: Exclude<PointQuality, "measured"> | null,
): Exclude<PointQuality, "measured"> | null {
  if (left === null) return right;
  if (right === null) return left;
  return SUBSTITUTE_ORDER.indexOf(left) >= SUBSTITUTE_ORDER.indexOf(right) ? left : right;
}

/** Die Rasterweite: angegeben, aus dem Raster abgeleitet, oder unbekannt. */
export function stepOf(props: Pick<ChartProps, "step" | "bucket">): number | undefined {
  if (props.step !== undefined) return props.step;
  return props.bucket === undefined ? undefined : BUCKET_MS[props.bucket];
}

/**
 * @param colors Woher die Reihen ihre Farbe nehmen — Token ohne Angabe; das
 *   Diagramm reicht für das Canvas die aufgelösten Werte und für die Legende
 *   die `var(…)` herein (HM-107).
 */
export function buildFrame(props: ChartProps, colors?: SeriesColorSource): ChartFrame {
  const paint = seriesColors(props.series, colors);
  const aligned = alignSeries(props.series, stepOf(props), props.cadence);

  const isRight = (index: number): boolean => props.series[index]!.axis === "right";
  const leftIndices = props.series.map((_, index) => index).filter((index) => !isRight(index));
  const rightIndices = props.series.map((_, index) => index).filter(isRight);
  /* Die Bedingungen der rechten Achse (ADR 0021, Nachtrag) — abgelehnt statt
     geraten: Eine rechte Reihe ohne feste Skala bekäme einen Maßstab, den die
     Daten wählen, und eine Fläche dort läse sich als Menge. */
  if (rightIndices.length > 0) {
    if (props.rightRange === undefined || props.rightFormat === undefined) {
      throw new Error("Eine Reihe auf der rechten Achse verlangt rightRange und rightFormat am Diagramm.");
    }
    if (rightIndices.some((index) => (props.series[index]!.mark ?? "line") !== "line")) {
      throw new Error("Auf der rechten Achse steht nur eine Linie — keine Fläche und kein Balken.");
    }
  }

  /** Gestapelt wird nur links; die rechte Linie gehört zu keinem Ganzen. */
  const stacked = props.stacked === true && leftIndices.length > 1;
  const stackedColumns = stacked
    ? stackValues(leftIndices.map((index) => aligned.series[index]!.values))
    : null;
  const columns = aligned.series.map((entry, index) => {
    const position = leftIndices.indexOf(index);
    return stackedColumns !== null && position >= 0 ? stackedColumns[position]! : entry.values;
  });

  const data: (readonly (number | null)[])[] = [aligned.times];
  const bands: BandSlot[] = [];
  /** Die Reihen in Zeichenreihenfolge, jede mit ihrem Platz in `props.series`. */
  const drawn: { readonly index: number; readonly slot: SeriesSlot }[] = [];

  /**
   * **Gestapelt wird von oben nach unten gezeichnet.** Die oberste Fläche ist
   * die größte; käme sie zuletzt, deckte sie alle darunter zu. Gerechnet wird
   * dagegen von unten nach oben — deshalb die Umkehrung erst hier.
   */
  const order = stacked ? [...leftIndices].reverse() : [...leftIndices];
  /* Rechts zuletzt: Die Linie liegt über den Balken, sonst verschwände sie hinter ihnen. */
  order.push(...rightIndices);

  for (const index of order) {
    const entry = props.series[index]!;
    const band = aligned.series[index]!.band;
    const color = paint[index]!;

    /**
     * **Ein Band gibt es nur ohne Stapelung.** Die Kanten müssten sonst
     * mitgestapelt werden, und die Spanne um eine Summe ist etwas anderes als
     * die Summe der Spannen — eine Rechnung, die niemand bestellt hat (Regel 9).
     */
    if (band !== null && !stacked && !isRight(index)) {
      const high = data.length;
      data.push(band.max, band.min);
      bands.push({ high, low: high + 1, color });
    }

    drawn.push({
      index,
      slot: {
        slot: data.length,
        id: entry.id,
        label: entry.label,
        color,
        mark: entry.mark ?? "line",
        // Steht erst nach der Schleife fest: Gestapelt wird von oben nach unten
        // gezeichnet, die Reihe darunter bekommt ihren Platz also später.
        beneath: null,
        coverage: aligned.series[index]!.coverage,
        quality: aligned.series[index]!.quality,
        axis: isRight(index) ? "right" : "left",
        dashed: entry.dashed === true,
      },
    });
    data.push(columns[index]!);
  }

  /**
   * **Was unter einer Reihe liegt, steckt in ihrer eigenen Spalte mit drin.**
   * Gerechnet wurde von unten nach oben: In `columns[i]` steht die Summe der
   * Reihen `0..i`, der eigene Wert ist die Differenz zur Reihe darunter. Das
   * Ablesefenster liest aus derselben Matrix, die gezeichnet wird, und braucht
   * deshalb den Weg zurück (`unstackValue`).
   */
  const slotOfSeries = new Map(drawn.map((entry) => [entry.index, entry.slot.slot]));
  const below = (index: number): number | null => {
    const position = leftIndices.indexOf(index);
    return position > 0 ? (slotOfSeries.get(leftIndices[position - 1]!) ?? null) : null;
  };
  const slots: readonly SeriesSlot[] = drawn.map((entry) => ({
    ...entry.slot,
    beneath: stacked && entry.slot.axis === "left" ? below(entry.index) : null,
  }));

  const leftSlots = slots.filter((slot) => slot.axis === "left");
  const rows = stacked
    ? [...[...leftSlots].reverse(), ...slots.filter((slot) => slot.axis === "right")]
    : slots;
  const left = leftIndices.map((index) => props.series[index]!);
  const empty = !data
    .slice(1)
    .some((column) => column.some((value) => value !== null && Number.isFinite(value)));

  return {
    data,
    slots,
    rows,
    bands,
    empty,
    baseline: stacked || left.some((entry) => entry.mark === "bars") ? "zero" : "auto",
    stacked,
    total: stacked ? (leftSlots[0]?.slot ?? null) : null,
    bucketed: left.length > 0 && left.every((entry) => entry.mark === "bars"),
    substituted: aligned.times.map((_, at) =>
      aligned.series.reduce<Exclude<PointQuality, "measured"> | null>(
        (worst, entry) => lessCertain(worst, entry.quality[at] ?? null),
        null,
      ),
    ),
  };
}
